import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { Info } from '@mui/icons-material';
import { ClickAwayListener, IconButton, Tooltip } from '@mui/material';

import { useTooltip } from '@ezbobdev/ui-commons';
import { useTranslation } from '@ezbobdev/ui-framework';

import MaterialTypography from '../MaterialTypography/MaterialTypography';

const stripBrTags = (content) => {
  if (typeof content !== 'string') return content;
  return content.replace(/<br\s*(?:[^>]*)?\/?>/gi, ' ').trim();
};

const MaterialTooltip = (props) => {
  const {
    title,
    configByName,
    windowIsOpen,
    handleClosing,
    toggleWindow,
    onBlurHandler,
    hasChildren,
    showArrow,
    placement,
    disableRipple,
    muiTypographyProps,
    customSize,
    translationData,
    children,
    tooltipContentId,
    iconColor,
  } = useTooltip(props, MaterialTooltip.displayName);

  const { t } = useTranslation();

  const tooltipContent = title || t(`${configByName}.tooltip.content`, { ...translationData });

  return useMemo(() => {
    return (
      <>
        <MaterialTypography srOnly id={tooltipContentId}>
          {stripBrTags(tooltipContent)}
        </MaterialTypography>
        {/* ClickAwayListener must wrap a single element that can hold a ref (not a Fragment),
            otherwise its internal nodeRef stays null and onClickAway never fires — leaving the
            tooltip stuck open on outside click/tap (GRN-7699). */}
        <ClickAwayListener onClickAway={handleClosing}>
          <Tooltip
            title={
              title || (
                <MaterialTypography variant="caption" {...muiTypographyProps}>
                  {tooltipContent}
                </MaterialTypography>
              )
            }
            enterTouchDelay={0}
            data-testid={`${configByName}-tooltip`}
            arrow={showArrow}
            placement={placement}
            disableInteractive={true}
            disableFocusListener={!hasChildren}
            disableHoverListener={!hasChildren}
            open={hasChildren ? undefined : windowIsOpen}
          >
            {hasChildren ? (
              <div>{children}</div>
            ) : (
              <IconButton
                disableRipple={disableRipple}
                onClick={toggleWindow}
                onBlur={onBlurHandler}
                aria-describedby={tooltipContentId}
                aria-expanded={windowIsOpen}
                aria-controls={tooltipContentId}
                aria-label={t('globals.tooltipTriggerLabel')}
                data-testid={`${configByName}-tooltipToggle`}
                className="tooltip-icon-button"
              >
                <Info sx={{ fontSize: customSize }} color={iconColor} />
              </IconButton>
            )}
          </Tooltip>
        </ClickAwayListener>
      </>
    );
  }, [
    windowIsOpen,
    configByName,
    placement,
    children,
    onBlurHandler,
    handleClosing,
    toggleWindow,
    tooltipContent,
    tooltipContentId,
    muiTypographyProps,
    t,
    showArrow,
    hasChildren,
    disableRipple,
    customSize,
    iconColor,
  ]);
};

MaterialTooltip.propsTypes = {
  placement: PropTypes.string,
  children: PropTypes.node,
};

MaterialTooltip.displayName = 'MaterialTooltip';

export default React.memo(MaterialTooltip);
